import { Dispatch, FC, SetStateAction, useContext } from "react";
import { PatternOfCareProviderLookupSearchModel } from "../interfaces";
import { UserContext } from "@app/user/UserContext";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import Roles from "@app/user/roles";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { FeatureFlagContext, FeatureFlagContextProps, Features } from "../../../../feature-flags";
import { checkForEnter } from "@app/shared/utility";

interface pocpplsearchboxProps {
  handleClear: () => void;
  handleFieldUpdate: (key: keyof PatternOfCareProviderLookupSearchModel, value: string) => void;
  handleSearch: () => void;
  isFiltered: boolean;
  isLoading: boolean;
  searchState: PatternOfCareProviderLookupSearchModel;
  setSearchState: Dispatch<SetStateAction<PatternOfCareProviderLookupSearchModel>>;
}

export const PatternOfCareProviderLookupSearchBox: FC<pocpplsearchboxProps> = (props: pocpplsearchboxProps & FeatureFlagContextProps) => {
  const user = useContext(UserContext);
  const { firstName, lastName, npi, tin, organizationName, address, city, state, zip } = props.searchState;
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const isNameSearchEnabled = featureFlags.has(Features.PATTERN_OF_CARE_NAME_SEARCH);
  const isLocationSearchEnabled = featureFlags.has(Features.PATTERN_OF_CARE_LOCATION_SEARCH);

  const isSearchDisabled = props.isLoading || (
    isNameSearchEnabled
      ? !firstName && !lastName && !npi.trim() && !tin.trim() && !organizationName && !address && !city && !state && !zip
      : !npi.trim() && !tin.trim()
  );
  const isClearDisbled = props.isLoading || !props.isFiltered;

  const requiredSearchLabel = "These fields search for an exact match. NPI" + (user.roles.includes(Roles.TIN_VIEWER) ? " or TIN" : "") + " is required.";

  const getSearchAndClearButtons = (
    <>
      <QESButton
        className="col-1"
        qesType="secondary"
        qesTestId="pattern-of-care-panel__search"
        disabled={isSearchDisabled}
        onClick={props.handleSearch}
      >
        {props.isLoading ? <QesIcon iconName={QesIcons.FontAwesome.Spinner} classNames="qes-icon-spin" description="Loading" /> : "Search"}
      </QESButton>

      <QESButton
        className="col-1"
        qesType="secondary-danger"
        qesTestId="pattern-of-care-panel__clear"
        disabled={isClearDisbled}
        onClick={props.handleClear}
      >
        Clear
      </QESButton>
    </>
  );

  return (
    <div className="pattern-of-care__input-fields">
      {!isNameSearchEnabled &&
        <p className="ml-3">{requiredSearchLabel}</p>
      }
      <div className="mb-3">
        <div className="col-10 d-flex">
          {isNameSearchEnabled &&
            <>
              <QESTextInput
                label="First Name"
                testId="pattern-of-care-panel__first-name"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("firstName", e.target.value) }}
                value={firstName}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="Last Name"
                testId="pattern-of-care-panel__last-name"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("lastName", e.target.value) }}
                value={lastName}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />
            </>
          }

          <QESTextInput
            label="NPI"
            testId="pattern-of-care-panel__npi"
            containerClassName="col-3"
            onChange={(e) => { props.handleFieldUpdate("npi", e.target.value) }}
            value={npi}
            onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
          />

          {user.roles.includes(Roles.TIN_VIEWER) && (
            <QESTextInput
              label="TIN"
              testId="pattern-of-care-panel__tin"
              containerClassName="col-3"
              onChange={(e) => { props.handleFieldUpdate("tin", e.target.value) }}
              value={tin}
              onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
            />
          )}

          {!isNameSearchEnabled && !isLocationSearchEnabled && getSearchAndClearButtons}
        </div>
      </div>
      <div className="d-flex">
        <div className="col-10 d-flex">
          {isNameSearchEnabled &&
            <>
              <QESTextInput
                label="Organization Name"
                testId="pattern-of-care-panel__organization-name"
                containerClassName="col"
                onChange={(e) => { props.handleFieldUpdate("organizationName", e.target.value) }}
                value={organizationName}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="Address"
                testId="pattern-of-care-panel__address"
                containerClassName="col"
                onChange={(e) => { props.handleFieldUpdate("address", e.target.value) }}
                value={address}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />
            </>
          }
          {isLocationSearchEnabled &&
            <>
              <QESTextInput
                label="City"
                testId="pattern-of-care-panel__city"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("city", e.target.value) }}
                value={city}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="State Abbreviation"
                testId="pattern-of-care-panel__state-abbreviation"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("state", e.target.value) }}
                pattern="/[a-zA-Z]{0,2}/"
                value={state}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />

              <QESTextInput
                label="Zip"
                testId="pattern-of-care-panel__zip"
                containerClassName="col-3"
                onChange={(e) => { props.handleFieldUpdate("zip", e.target.value) }}
                pattern="/\d{0,5}/"
                value={zip}
                onKeyUp={(e) => !isSearchDisabled && checkForEnter(e, props.handleSearch)}
              />
            </>
          }

          {(isNameSearchEnabled || isLocationSearchEnabled) && getSearchAndClearButtons}
        </div>
      </div>
    </div>
  );
}